import { StatusData } from "@fairfleet/geotab";
import { useMemo } from "react";
import { LiveDataRow } from "../models/LiveDataRow";
import { DisplayUnit, LiveDataLookups, toLiveDataRow } from "../models/toLiveDataRow";
import { useLiveStatusData } from "./useLiveStatusData";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useDiagnosticsByIds } from "@/hooks/diagnostic/useDiagnosticsByIds";
import { useDayStatusData } from "@/hooks/statusData/useDayStatusData";
import useDisplayMeasurementProfileDetail from "@/hooks/useDisplayMeasurementProfileDetail";
import { useUserDisplayProfile } from "@/hooks/users/useUserDisplayProfile";
import { parseGeotabId } from "@/utilities/parseGeotabId";

export function useLiveDataRows(
  deviceId: string | undefined,
  minutesBack: number = 5,
  refetchInterval: number = 10000,
) {
  const {
    data: statusData,
    isLoading,
    isFetching,
  } = useLiveStatusData(deviceId, minutesBack, refetchInterval);
  // What the checklist falls back to when the live window has nothing: the last 24 hours.
  const { data: fallbackStatusData } = useDayStatusData(deviceId);
  const { me } = useGeotabClient();

  const { data: userDisplayProfile } = useUserDisplayProfile(me?.id);
  const { data: diagnosticUnits = [] } = useDisplayMeasurementProfileDetail(
    userDisplayProfile?.profileId ?? undefined,
  );

  // Sorted and deduplicated, because this array is part of the diagnostics query key. Left in
  // arrival order it changes whenever a rarely reported diagnostic enters or leaves the sliding
  // live window, and every diagnostic on the vehicle is fetched again.
  const diagnosticIds = useMemo(
    () => sortedDiagnosticIds(statusData, fallbackStatusData),
    [statusData, fallbackStatusData],
  );

  const { data: diagnostics = {}, isLoading: diagnosticsLoading } =
    useDiagnosticsByIds(diagnosticIds);

  const lookups: LiveDataLookups = useMemo(
    () => ({
      diagnostics,
      units: unitsByDiagnosticId(diagnosticUnits),
      sources: sourcesByDiagnosticId(diagnostics),
    }),
    [diagnostics, diagnosticUnits],
  );

  const rows: LiveDataRow[] = useMemo(
    () => (statusData ?? []).map(sd => toLiveDataRow(sd, lookups)),
    [statusData, lookups],
  );

  const fallbackRows: LiveDataRow[] = useMemo(
    () => (fallbackStatusData ?? []).map(sd => toLiveDataRow(sd, lookups)),
    [fallbackStatusData, lookups],
  );

  return { rows, fallbackRows, isLoading, isFetching, diagnosticsLoading };
}

function sortedDiagnosticIds(...pages: (StatusData[] | undefined)[]): string[] {
  const ids = new Set<string>();

  for (const page of pages) {
    for (const record of page ?? []) {
      const id = record.diagnostic?.id;
      if (id) {
        ids.add(id);
      }
    }
  }

  return [...ids].sort();
}

function unitsByDiagnosticId(
  details: ReturnType<typeof useDisplayMeasurementProfileDetail>["data"] & object,
): Record<string, DisplayUnit> {
  const units: Record<string, DisplayUnit> = {};

  for (const detail of details) {
    const unit = detail.displayUnitOfMeasure;
    units[detail.diagnosticId] = {
      name: unit?.name ?? "",
      factorFromBase: unit?.factorFromBase,
      offsetFromBase: unit?.offsetFromBase,
    };
  }

  return units;
}

function sourcesByDiagnosticId(
  diagnostics: Record<string, { source?: unknown }>,
): Record<string, string> {
  const sources: Record<string, string> = {};

  for (const [diagnosticId, diagnostic] of Object.entries(diagnostics)) {
    sources[diagnosticId] = parseGeotabId(String(diagnostic.source ?? ""), "Source");
  }

  return sources;
}
